'use client'

import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'

import { cn } from '../lib/utils'

export const buttonVariants = cva(
	"relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap border font-medium text-base outline-none transition-colors pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 sm:text-sm [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:shrink-0",
	{
		defaultVariants: {
			size: 'default',
			variant: 'default',
		},
		variants: {
			size: {
				default: 'h-9 px-[calc(--spacing(3)-1px)] sm:h-8',
				icon: 'size-9 sm:size-8',
				'icon-lg': 'size-10 sm:size-9',
				'icon-sm': 'size-8 sm:size-7',
				'icon-xl': "size-11 sm:size-10 [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
				'icon-xs':
					"size-7 sm:size-6 not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-4 sm:not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
				lg: 'h-10 px-[calc(--spacing(3.5)-1px)] sm:h-9',
				sm: 'h-8 gap-1.5 px-[calc(--spacing(2.5)-1px)] sm:h-7',
				xl: "h-11 px-[calc(--spacing(4)-1px)] text-lg sm:h-10 sm:text-base [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
				xs: "h-7 gap-1 px-[calc(--spacing(2)-1px)] text-sm sm:h-6 sm:text-xs [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5",
			},
			variant: {
				default: 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 data-pressed:bg-primary/90',
				destructive:
					'border-destructive bg-destructive text-white hover:bg-destructive/90 data-pressed:bg-destructive/90',
				'destructive-outline':
					'border-input bg-popover text-destructive-foreground hover:border-destructive/32 hover:bg-destructive/4 data-pressed:border-destructive/32 data-pressed:bg-destructive/4',
				ghost: 'border-transparent text-foreground hover:bg-accent data-pressed:bg-accent',
				link: 'border-transparent underline-offset-4 hover:underline data-pressed:underline',
				outline: 'border-input bg-popover text-foreground hover:bg-accent/50 data-pressed:bg-accent/50',
				secondary:
					'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90 data-pressed:bg-secondary/90',
			},
		},
	},
)

export interface ButtonProps extends useRender.ComponentProps<'button'> {
	variant?: VariantProps<typeof buttonVariants>['variant']
	size?: VariantProps<typeof buttonVariants>['size']
}

export function Button({ className, variant, size, render, ...props }: ButtonProps): React.ReactElement {
	const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>['type'] = render ? undefined : 'button'

	const defaultProps = {
		className: cn(buttonVariants({ className, size, variant })),
		'data-slot': 'button',
		'data-size': size,
		'data-variant': variant,
		type: typeValue,
	}

	return useRender({
		defaultTagName: 'button',
		props: mergeProps<'button'>(defaultProps, props),
		render,
	})
}
